import { Button } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import AuthorizationLetterField from "../Inputs/AuthorizationLetterField";
import AuthorizedUserInput from "../Inputs/AuthorizedUserInput";
import FileField from "../Inputs/FileField";
import ListCheckboxes from "../Inputs/ListCheckboxes";
import RadioGroupField from "../Inputs/RadioGroupField";
import "../Inputs/styles/list-checkboxes.css";
import "../Inputs/styles/new-text.css";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper";
import "../styles/form-container.css";
import {
  useAuthorizationLetters,
  useDeleteLetter,
  useGenerateLetter,
  useUploadSignedLetter,
} from "./Hooks/authorizationLetterQueries";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";
import "./styles/services.css";

// Slide 4: Dust Services
const DustServices = ({ slideState, formStateLoaded }) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const {
    slide1,
    slide4,
    setSlide4,
    slide11,
    setSlide11,
    setIsLoading,
    requestStatus,
    populateInvalidInputs,
    disabledInputCheck,
    setSlideStatus,
    setPageNumber,
  } = useContext(FormContext);

  const { client, clientId, projectId } = useContext(DashboardContext);

  const { data: authLetters, isFetched: lettersFetched } =
    useAuthorizationLetters(clientId);

  const generateAuthorizationLetter = useGenerateLetter(setSlide4);
  const deleteLetter = useDeleteLetter(setSlide4);
  const uploadSignedLetter = useUploadSignedLetter(
    setSlide4,
    clientId,
    projectId,
  );

  // On Mount, the page should scroll to the top
  useEffect(() => {
    if (client && client.shortName === "Greystone") {
      setPageNumber(5);
    }
    scrollToFirstRequiredField(slide4);
  }, []);

  // Update Slide State
  const slide4Mutation = useSetSlide(
    clientId,
    projectId,
    4,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "4",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <RadioGroupField
            idProp="airQualityServicesRequest"
            label="Are you interested in Air Quality Services?"
            state={slide4}
            setState={setSlide4}
            setIsLoading={setIsLoading}
            debouncedSave={slide4Mutation}
            disabledInput={false}
          />
          {slide1.distAcr && +slide1.distAcr >= 50 && (
            <p className="important-text bold">
              {`Per the Department of Air Quality (DAQ) regulations all projects disturbing 50 or more acres are required to have a certified dust monitor onsite during any construction activities`}
            </p>
          )}
          {slide4.airQualityServicesRequest !== "No" && (
            <>
              <div className="input-list-checkbox-container">
                <ResponsiveRow type="close-center">
                  <ListCheckboxes
                    label="File Permit"
                    idProp="filePermitRequest"
                    state={slide4}
                    setState={setSlide4}
                    val={slide4.filePermitRequest}
                    disabledInput={false}
                    setIsLoading={setIsLoading}
                    debouncedSave={slide4Mutation}
                  />
                  <ListCheckboxes
                    label="Modify/Renew Permit"
                    idProp="amendPermitRequest"
                    state={slide4}
                    setState={setSlide4}
                    val={slide4.amendPermitRequest}
                    disabledInput={false}
                    setIsLoading={setIsLoading}
                    debouncedSave={slide4Mutation}
                  />
                  <ListCheckboxes
                    label="Dust Sign"
                    idProp="dustSignRequest"
                    state={slide4}
                    setState={setSlide4}
                    val={slide4.dustSignRequest}
                    disabledInput={false}
                    setIsLoading={setIsLoading}
                    debouncedSave={slide4Mutation}
                  />
                  <ListCheckboxes
                    label="Dust Monitoring"
                    idProp="dustMonitoringRequest"
                    state={slide4}
                    setState={setSlide4}
                    val={slide4.dustMonitoringRequest}
                    disabledInput={false}
                    setIsLoading={setIsLoading}
                    debouncedSave={slide4Mutation}
                  />
                  <ListCheckboxes
                    label="Dust Control Services"
                    idProp="waterServiceRequest"
                    state={slide4}
                    setState={setSlide4}
                    val={slide4.waterServiceRequest}
                    disabledInput={false}
                    setIsLoading={setIsLoading}
                    debouncedSave={slide4Mutation}
                  />
                  <ListCheckboxes
                    label="Dust Audit"
                    idProp="dustAuditRequest"
                    state={slide4}
                    setState={setSlide4}
                    val={slide4.dustAuditRequest}
                    disabledInput={false}
                    setIsLoading={setIsLoading}
                    debouncedSave={slide4Mutation}
                  />
                </ResponsiveRow>
              </div>
            </>
          )}
          {slide4.filePermitRequest || slide4.amendPermitRequest ? (
            <>
              <h2 className="question-section">DAQ Authorization Letter</h2>
              <p className="important-text">
                To allow Argus to manage your Department of Air Quality (DAQ)
                permits, a signed authorization letter with a wet signature must
                be on file for the responsible official. You can either select
                an existing authorization letter or complete the form below to
                generate an unsigned version. Once signed, please upload the
                completed letter. This letter is necessary to submit this form.
              </p>
              {lettersFetched && authLetters && authLetters.length > 0 && (
                <>
                  <ResponsiveRow type="center">
                    <AuthorizationLetterField
                      idProp="authorizationLetter"
                      label="Existing Authorization Letter"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4["authorizationLetter"]["id"]}
                      setIsLoading={setIsLoading}
                      disabledInput={false}
                      disabledInputMessage={""}
                      invalidData={null}
                      authLetters={authLetters}
                      isFetched={lettersFetched}
                    />
                  </ResponsiveRow>
                  {slide4.authorizationLetter["id"] &&
                  slide4.authorizationLetter["isSigned"] &&
                  slide4.authorizationLetter["letter"] ? (
                    <Button
                      className="download-btn"
                      variant="contained"
                      id="download"
                      onClick={(e) => {
                        e.preventDefault();
                        let url = slide4.authorizationLetter.letter;
                        window.open(url);
                      }}
                    >
                      Download
                    </Button>
                  ) : null}
                </>
              )}
              {!slide4.authorizationLetter["isSigned"] ? (
                <>
                  <p className="important-text">
                    Fill out the information below to generate a template
                    authorization letter to download.
                  </p>
                  <ResponsiveRow type="center">
                    <AuthorizedUserInput
                      idProp="companyName"
                      label={"Company Name"}
                      val={slide4.authorizationLetter["companyName"]}
                      state={slide4}
                      setState={setSlide4}
                      req={
                        !slide4.authorizationLetter["companyName"] ||
                        slide4.authorizationLetter["companyName"] === "" ||
                        slide4.authorizationLetter["companyName"].length < 3
                      }
                      setIsLoading={setIsLoading}
                      disabledInput={
                        slide4.authorizationLetter["id"] &&
                        slide4.authorizationLetter["isSigned"]
                      }
                      disabledInputMessage={""}
                      invalidData={null}
                      readOnly={slide4.authorizationLetter["letter"]}
                      type="company"
                    />
                    <AuthorizedUserInput
                      idProp="signer"
                      label={"Responsible Official"}
                      val={slide4.authorizationLetter["signer"]}
                      state={slide4}
                      setState={setSlide4}
                      req={
                        slide4.authorizationLetter["signer"] === "" ||
                        slide4.authorizationLetter["signer"].split(" ").length <
                          2
                      }
                      setIsLoading={setIsLoading}
                      disabledInput={
                        slide4.authorizationLetter["id"] &&
                        slide4.authorizationLetter["isSigned"]
                      }
                      disabledInputMessage={""}
                      invalidData={null}
                      readOnly={slide4.authorizationLetter["letter"]}
                      type="name"
                    />
                    <AuthorizedUserInput
                      idProp="title"
                      label={"Responsible Official's Title"}
                      val={slide4.authorizationLetter["title"]}
                      state={slide4}
                      setState={setSlide4}
                      req={false}
                      setIsLoading={setIsLoading}
                      disabledInput={
                        slide4.authorizationLetter["id"] &&
                        slide4.authorizationLetter["isSigned"]
                      }
                      disabledInputMessage={""}
                      invalidData={null}
                      readOnly={slide4.authorizationLetter["letter"]}
                      type="title"
                    />
                  </ResponsiveRow>
                  <div className="authorization-btns">
                    <Button
                      variant="contained"
                      className="clear-fields-btn"
                      id="clear"
                      onClick={async () => {
                        if (
                          !slide4.authorizationLetter["id"] &&
                          !slide4.authorizationLetter["letter"]
                        ) {
                          setSlide4({
                            ...slide4,
                            authorizationLetter: {
                              id: "",
                              isSigned: false,
                              letter: "",
                              companyName: "",
                              signer: "",
                              title: "",
                            },
                          });
                        } else {
                          await deleteLetter.mutateAsync({
                            projectId,
                            letterId: slide4.authorizationLetter["id"],
                          });
                        }
                      }}
                      disabled={
                        (!slide4.authorizationLetter["company"] &&
                          !slide4.authorizationLetter["signer"] &&
                          !slide4.authorizationLetter["title"]) ||
                        requestStatus === "RV"
                      }
                    >
                      Clear
                    </Button>
                    {!slide4.authorizationLetter["id"] &&
                      !slide4.authorizationLetter["letter"] && (
                        <Button
                          variant="contained"
                          className="generate-btn"
                          id="generate"
                          onClick={async () => {
                            await generateAuthorizationLetter.mutateAsync({
                              clientId,
                              projectId,
                              signer: slide4.authorizationLetter["signer"],
                              company:
                                slide4.authorizationLetter["companyName"],
                              title: slide4.authorizationLetter["title"],
                            });
                          }}
                          disabled={
                            !slide4.authorizationLetter["companyName"] ||
                            !slide4.authorizationLetter["signer"]
                          }
                        >
                          Generate
                        </Button>
                      )}
                    {slide4.authorizationLetter["letter"] && (
                      <>
                        <Button
                          variant="contained"
                          className="download-btn"
                          id="download"
                          onClick={(e) => {
                            e.preventDefault();
                            let url = slide4.authorizationLetter.letter;
                            window.open(url);
                          }}
                        >
                          Download
                        </Button>
                        <Button
                          variant="contained"
                          className="upload-btn"
                          id="upload"
                          onClick={(e) => {
                            document.getElementById("upload-auth").click();
                          }}
                          disabled={
                            requestStatus === "P" ||
                            requestStatus === "RV" ||
                            (slide4.authorizationLetter["letter"] &&
                              slide4.authorizationLetter["isSigned"])
                          }
                        >
                          Upload Signed
                        </Button>
                        <input
                          hidden
                          type="file"
                          id="upload-auth"
                          onChange={async (e) => {
                            const letter = e.target.files[0];
                            await uploadSignedLetter.mutateAsync({
                              letterId: slide4.authorizationLetter["id"],
                              letter,
                            });
                          }}
                        />
                      </>
                    )}
                  </div>
                </>
              ) : null}
            </>
          ) : null}
          {slide4.airQualityServicesRequest !== "No" &&
            (slide4.filePermitRequest === true ||
              slide4.amendPermitRequest === true) && (
              <>
                <h2 className="question-section">Water Sources</h2>
                <p className="important-text">
                  Please select the water sources to be used as a part of the
                  dust control measures for this project
                </p>
                <div className="input-list-checkbox-container">
                  <ResponsiveRow type={"close-center"}>
                    <ListCheckboxes
                      label="Fire Hose"
                      idProp="fireHose"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4.fireHose}
                      disabledInput={false}
                      setIsLoading={setIsLoading}
                      debouncedSave={slide4Mutation}
                    />
                    <ListCheckboxes
                      label="Hydrant w/ Jones Valve"
                      idProp="hydrantJonesValve"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4.hydrantJonesValve}
                      disabledInput={false}
                      setIsLoading={setIsLoading}
                      debouncedSave={slide4Mutation}
                    />
                    <ListCheckboxes
                      label="Pond"
                      idProp="pond"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4.pond}
                      disabledInput={false}
                      setIsLoading={setIsLoading}
                      debouncedSave={slide4Mutation}
                    />
                    <ListCheckboxes
                      label="Stand Tank"
                      idProp="standTank"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4.standTank}
                      disabledInput={false}
                      setIsLoading={setIsLoading}
                      debouncedSave={slide4Mutation}
                    />
                    <ListCheckboxes
                      label="Water Truck"
                      idProp="waterTruck"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4.waterTruck}
                      disabledInput={false}
                      setIsLoading={setIsLoading}
                      debouncedSave={slide4Mutation}
                    />
                    <ListCheckboxes
                      label="Well"
                      idProp="well"
                      state={slide4}
                      setState={setSlide4}
                      val={slide4.well}
                      disabledInput={false}
                      setIsLoading={setIsLoading}
                      debouncedSave={slide4Mutation}
                    />
                  </ResponsiveRow>
                </div>
              </>
            )}
          {slide4.permitPreparationRequest ||
          slide4.filePermitRequest ||
          slide4.amendPermitRequest ? (
            <FileField
              idProp={"areaOfDisturbance"}
              label={"Area of Disturbance Map"}
              state={slide11}
              setState={setSlide11}
              disabledInput={disabledInputCheck(
                "areaOfDisturbance",
                disabledInputs,
              )}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
            />
          ) : null}
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default DustServices;
